/**
 * Equipment Detail Panel
 * Shows live telemetry tiles, time-series charts, health breakdown, predictive maintenance, and alert history.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowLeft, RefreshCw, Activity, AlertTriangle, Shield, Clock,
  Wrench, ChevronDown, ChevronUp, FileText, X, CheckCircle2, Timer
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, RadarChart, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, Radar, Legend
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const HEALTH_COLOR = (score) => score >= 80 ? '#22C55E' : score >= 60 ? '#F59E0B' : '#EF4444';
const STATUS_COLOR = { GOOD: '#22C55E', WARNING: '#F59E0B', CRITICAL: '#EF4444' };
const CONDITION_COLOR = { GOOD: '#22C55E', FAIR: '#F59E0B', POOR: '#EF4444', CRITICAL: '#DC2626' };

function TelemetryTile({ param, lastUpdate }) {
  const color = STATUS_COLOR[param.status] || '#6B7280';
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    setElapsed(0);
    const t = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(t);
  }, [lastUpdate, param.value]);
  return (
    <div className="rounded-lg p-3" data-testid={`param-tile-${param.key}`}
      style={{ backgroundColor: param.status === 'CRITICAL' ? '#EF444408' : param.status === 'WARNING' ? '#F59E0B08' : 'var(--hover-bg)', border: `1px solid ${param.status === 'CRITICAL' ? '#EF444430' : param.status === 'WARNING' ? '#F59E0B30' : 'var(--border-color)'}` }}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{param.name}</span>
        <div className="flex items-center gap-1">
          <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${color}15`, color }}>{param.status}</span>
        </div>
      </div>
      <div className="text-lg font-bold font-mono" style={{ color }}>
        {typeof param.value === 'number' ? param.value.toFixed(1) : param.value || '—'}
        <span className="text-xs font-normal ml-1" style={{ color: 'var(--text-disabled)' }}>{param.unit}</span>
      </div>
      <div className="flex items-center justify-between mt-1">
        <div className="h-1 flex-1 rounded-full overflow-hidden mr-2" style={{ backgroundColor: 'var(--border-color)' }}>
          <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, param.score || 0)}%`, backgroundColor: color }} />
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0" data-testid={`ticker-${param.key}`}>
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${elapsed < 5 ? 'animate-pulse' : ''}`} style={{ backgroundColor: elapsed < 5 ? '#22C55E' : elapsed < 15 ? '#F59E0B' : '#EF4444' }} />
          <span className="text-[9px] font-mono tabular-nums" style={{ color: 'var(--text-disabled)', minWidth: '24px' }}>{elapsed}s</span>
        </div>
      </div>
    </div>
  );
}

const TIME_OPTIONS = [
  { label: '1h', value: 1 }, { label: '6h', value: 6 },
  { label: '24h', value: 24 }, { label: '7d', value: 168 },
];
const CHART_COLORS = ['#3B82F6', '#EF4444', '#22C55E', '#F59E0B'];

function TimeSeriesSection({ projectId, equipmentCode, params }) {
  const [hours, setHours] = useState(1);
  const [selectedParams, setSelectedParams] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (params?.length > 0 && selectedParams.length === 0) {
      setSelectedParams([params[0].key]);
    }
  }, [params]);

  const fetchHistory = useCallback(async () => {
    if (!equipmentCode || selectedParams.length === 0) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/iot/equipment/${equipmentCode}/telemetry/history?hours=${hours}&params=${selectedParams.join(',')}`);
      const formatted = (data.history || []).map(h => {
        const time = new Date(h.timestamp);
        return { time: hours <= 6 ? time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : time.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit' }), ...h.readings };
      });
      setChartData(formatted);
    } catch {}
    setLoading(false);
  }, [projectId, equipmentCode, hours, selectedParams]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const toggleParam = (key) => {
    setSelectedParams(prev => {
      if (prev.includes(key)) return prev.filter(k => k !== key);
      if (prev.length >= 3) return [...prev.slice(1), key];
      return [...prev, key];
    });
  };

  return (
    <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Time-Series Trends</h3>
        <div className="flex items-center gap-1">
          {TIME_OPTIONS.map(o => (
            <button key={o.value} onClick={() => setHours(o.value)}
              className="text-[10px] px-2 py-1 rounded-md transition-all"
              style={{ backgroundColor: hours === o.value ? '#3B82F615' : 'transparent', color: hours === o.value ? '#3B82F6' : 'var(--text-muted)', fontWeight: hours === o.value ? '600' : '400' }}
              data-testid={`ts-${o.label}`}>
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-1 mb-3">
        {(params || []).map((p, i) => (
          <button key={p.key} onClick={() => toggleParam(p.key)}
            className="text-[10px] px-2 py-0.5 rounded-full transition-all"
            style={{
              backgroundColor: selectedParams.includes(p.key) ? `${CHART_COLORS[selectedParams.indexOf(p.key)]}20` : 'var(--hover-bg)',
              color: selectedParams.includes(p.key) ? CHART_COLORS[selectedParams.indexOf(p.key)] : 'var(--text-muted)',
              border: `1px solid ${selectedParams.includes(p.key) ? CHART_COLORS[selectedParams.indexOf(p.key)] + '40' : 'var(--border-color)'}`,
            }}>
            {p.name}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-8"><RefreshCw className="w-4 h-4 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>
      ) : chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
            <XAxis dataKey="time" tick={{ fontSize: 9 }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', fontSize: 11 }} />
            {selectedParams.map((key, i) => (
              <Line key={key} type="monotone" dataKey={key} stroke={CHART_COLORS[i]} dot={false} strokeWidth={2} name={params?.find(p => p.key === key)?.name || key} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div className="text-center py-8 text-xs" style={{ color: 'var(--text-muted)' }}>No data for selected parameters</div>
      )}
    </div>
  );
}

function HealthBreakdown({ params }) {
  const radarData = (params || []).filter(p => p.score !== undefined).map(p => ({
    param: p.name.length > 12 ? p.name.slice(0, 12) + '...' : p.name,
    score: p.score,
    fullMark: 100,
  }));
  return (
    <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
      <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Health Score Breakdown</h3>
      {radarData.length > 0 ? (
        <ResponsiveContainer width="100%" height={250}>
          <RadarChart data={radarData}>
            <PolarGrid stroke="var(--border-color)" />
            <PolarAngleAxis dataKey="param" tick={{ fontSize: 8, fill: 'var(--text-muted)' }} />
            <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 8 }} />
            <Radar name="Health" dataKey="score" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.2} />
          </RadarChart>
        </ResponsiveContainer>
      ) : <div className="text-xs text-center py-8" style={{ color: 'var(--text-muted)' }}>No data</div>}
    </div>
  );
}

function WorkOrderModal({ recommendation, equipmentCode, projectId, onClose, onCreated }) {
  const [form, setForm] = useState({
    description: recommendation?.action || '',
    notes: '',
    assignedTo: '',
    urgency: recommendation?.urgency || 'MEDIUM',
  });
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async () => {
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/iot/equipment/${equipmentCode}/work-order`, {
        description: form.description,
        urgency: form.urgency,
        recommendedAction: recommendation?.action || form.description,
        notes: form.notes,
        assignedTo: form.assignedTo,
      });
      toast.success(`Work order ${data.workOrderId} created`);
      onCreated(data);
    } catch { toast.error('Failed to create work order'); }
    setSubmitting(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="rounded-xl shadow-2xl w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()}
        style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="wo-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <FileText className="w-4 h-4" style={{ color: '#143A6B' }} /> Create Work Order
          </h3>
          <button onClick={onClose}><X className="w-4 h-4" style={{ color: 'var(--text-muted)' }} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] font-semibold block mb-1" style={{ color: 'var(--text-muted)' }}>Description *</label>
            <textarea className="w-full text-xs rounded-lg p-2 resize-none" rows={3}
              style={{ backgroundColor: 'var(--hover-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
              value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Describe the maintenance work required..." data-testid="wo-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-semibold block mb-1" style={{ color: 'var(--text-muted)' }}>Urgency</label>
              <select className="w-full text-xs rounded-lg p-2" value={form.urgency}
                style={{ backgroundColor: 'var(--hover-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                onChange={e => setForm(f => ({ ...f, urgency: e.target.value }))} data-testid="wo-urgency">
                {['IMMEDIATE', 'HIGH', 'MEDIUM', 'LOW'].map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold block mb-1" style={{ color: 'var(--text-muted)' }}>Assigned To</label>
              <input className="w-full text-xs rounded-lg p-2" placeholder="Name or team"
                style={{ backgroundColor: 'var(--hover-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                value={form.assignedTo} onChange={e => setForm(f => ({ ...f, assignedTo: e.target.value }))} data-testid="wo-assigned" />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-semibold block mb-1" style={{ color: 'var(--text-muted)' }}>Additional Notes</label>
            <textarea className="w-full text-xs rounded-lg p-2 resize-none" rows={2}
              style={{ backgroundColor: 'var(--hover-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
              value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Any additional notes..." data-testid="wo-notes" />
          </div>
          {recommendation && (
            <div className="text-[10px] p-2 rounded-lg" style={{ backgroundColor: 'var(--hover-bg)', color: 'var(--text-muted)' }}>
              <span className="font-semibold">AI Recommendation:</span> {recommendation.action}
              <span className="ml-2">Est. downtime: {recommendation.estimatedDowntime}</span>
              <span className="ml-2">Cost: {recommendation.costImpact}</span>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSubmit} disabled={submitting} data-testid="wo-submit-btn">
            {submitting ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <FileText className="w-3 h-3 mr-1" />}
            Create Work Order
          </Button>
        </div>
      </div>
    </div>
  );
}

function WorkOrderDetails({ workOrder, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="rounded-xl shadow-2xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()}
        style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="wo-details-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <CheckCircle2 className="w-4 h-4" style={{ color: '#22C55E' }} /> Work Order Created
          </h3>
          <button onClick={onClose}><X className="w-4 h-4" style={{ color: 'var(--text-muted)' }} /></button>
        </div>
        <div className="space-y-2">
          {[
            { label: 'Work Order ID', value: workOrder.workOrderId, highlight: true },
            { label: 'Equipment', value: `${workOrder.equipmentName} (${workOrder.equipmentCode})` },
            { label: 'Category', value: workOrder.category?.replace(/_/g, ' ') },
            { label: 'Description', value: workOrder.description },
            { label: 'Urgency', value: workOrder.urgency },
            { label: 'Recommended Action', value: workOrder.recommendedAction },
            { label: 'Assigned To', value: workOrder.assignedTo || '—' },
            { label: 'Notes', value: workOrder.notes || '—' },
            { label: 'Status', value: workOrder.status },
            { label: 'Created', value: new Date(workOrder.createdAt).toLocaleString() },
          ].map((row, i) => (
            <div key={i} className="flex text-xs">
              <span className="w-32 flex-shrink-0 font-medium" style={{ color: 'var(--text-muted)' }}>{row.label}</span>
              <span className={row.highlight ? 'font-bold font-mono' : ''} style={{ color: row.highlight ? '#3B82F6' : 'var(--text-primary)' }}>{row.value}</span>
            </div>
          ))}
        </div>
        <div className="flex justify-end">
          <Button size="sm" variant="outline" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}

function PredictivePanel({ projectId, equipmentCode }) {
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [woModal, setWoModal] = useState(null); // recommendation object
  const [createdWo, setCreatedWo] = useState(null); // created work order details

  const fetch = useCallback(async () => {
    if (!equipmentCode) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/iot/equipment/${equipmentCode}/maintenance/predicted`);
      setPrediction(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [projectId, equipmentCode]);

  useEffect(() => { fetch(); }, [fetch]);

  return (
    <div className="rounded-xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
      <button onClick={() => setExpanded(!expanded)} className="flex items-center justify-between w-full p-4">
        <div className="flex items-center gap-2">
          <Wrench className="w-4 h-4" style={{ color: '#143A6B' }} />
          <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Predictive Maintenance (AI)</span>
          {loading && <RefreshCw className="w-3 h-3 animate-spin" style={{ color: 'var(--text-muted)' }} />}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4" style={{ color: 'var(--text-muted)' }} /> : <ChevronDown className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />}
      </button>
      {expanded && prediction && (
        <div className="px-4 pb-4 space-y-3">
          {/* Summary */}
          <div className="text-xs p-3 rounded-lg" style={{ backgroundColor: 'var(--hover-bg)', color: 'var(--text-primary)' }}>
            {prediction.summary}
          </div>
          {/* Failure Probability */}
          <div className="grid grid-cols-3 gap-2">
            {[{ label: '7-day', val: prediction.failureProbability7d }, { label: '14-day', val: prediction.failureProbability14d }, { label: '30-day', val: prediction.failureProbability30d }].map(f => (
              <div key={f.label} className="text-center p-2 rounded-lg" style={{ backgroundColor: 'var(--hover-bg)' }}>
                <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Failure Prob ({f.label})</div>
                <div className="text-lg font-bold" style={{ color: HEALTH_COLOR(100 - (f.val || 0)) }}>{f.val || 0}%</div>
              </div>
            ))}
          </div>
          {/* Components */}
          {prediction.components?.length > 0 && (
            <div>
              <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Component Health</div>
              <div className="space-y-1">
                {prediction.components.map((c, i) => (
                  <div key={i} className="flex items-center justify-between text-xs p-2 rounded-lg" style={{ backgroundColor: 'var(--hover-bg)' }}>
                    <div>
                      <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{c.name}</span>
                      <span className="ml-2" style={{ color: 'var(--text-muted)' }}>RUL: {c.rul_hours}h</span>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ backgroundColor: `${CONDITION_COLOR[c.condition] || '#6B7280'}15`, color: CONDITION_COLOR[c.condition] || '#6B7280' }}>
                      {c.condition}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Recommendations */}
          {prediction.recommendations?.length > 0 && (
            <div>
              <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Maintenance Recommendations</div>
              {prediction.recommendations.map((r, i) => (
                <div key={i} className="flex items-center justify-between text-xs p-2 rounded-lg mb-1" style={{ backgroundColor: 'var(--hover-bg)' }}>
                  <div className="flex-1">
                    <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{r.action}</span>
                    <div className="flex items-center gap-2 mt-0.5" style={{ color: 'var(--text-muted)' }}>
                      <span className="px-1.5 py-0.5 rounded text-[9px]" style={{ backgroundColor: r.urgency === 'IMMEDIATE' ? '#EF444415' : r.urgency === 'HIGH' ? '#F59E0B15' : '#3B82F615', color: r.urgency === 'IMMEDIATE' ? '#EF4444' : r.urgency === 'HIGH' ? '#F59E0B' : '#3B82F6' }}>
                        {r.urgency}
                      </span>
                      <span>{r.estimatedDowntime}</span>
                      <span>Cost: {r.costImpact}</span>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="h-6 text-[10px] ml-2" onClick={() => setWoModal(r)} data-testid={`wo-btn-${i}`}>
                    <FileText className="w-3 h-3 mr-0.5" /> Work Order
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {/* Work Order Modal */}
      {woModal && (
        <WorkOrderModal
          recommendation={woModal}
          equipmentCode={equipmentCode}
          projectId={projectId}
          onClose={() => setWoModal(null)}
          onCreated={(wo) => { setWoModal(null); setCreatedWo(wo); }}
        />
      )}
      {/* Work Order Details */}
      {createdWo && (
        <WorkOrderDetails workOrder={createdWo} onClose={() => setCreatedWo(null)} />
      )}
    </div>
  );
}

function AlertHistory({ projectId, equipmentCode }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/v1/projects/${projectId}/iot/equipment/${equipmentCode}/alerts/history?days=30`);
        setAlerts(data.alerts || []);
      } catch {}
      setLoading(false);
    })();
  }, [projectId, equipmentCode]);

  return (
    <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
      <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Alert History (30 days)</h3>
      {loading ? (
        <div className="flex justify-center py-4"><RefreshCw className="w-4 h-4 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>
      ) : alerts.length > 0 ? (
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {alerts.slice(0, 50).map((a, i) => (
            <div key={i} className="flex items-center gap-2 text-[11px] py-1.5 px-2 rounded" style={{ borderBottom: '1px solid var(--border-color)' }}>
              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: a.severity === 'CRITICAL' ? '#EF4444' : a.severity === 'WARNING' ? '#F59E0B' : '#3B82F6' }} />
              <span className="font-mono" style={{ color: 'var(--text-disabled)' }}>{new Date(a.timestamp).toLocaleDateString()}</span>
              <span style={{ color: 'var(--text-primary)' }}>{a.parameterName}</span>
              <span className="font-mono" style={{ color: STATUS_COLOR[a.severity === 'CRITICAL' ? 'CRITICAL' : a.severity === 'WARNING' ? 'WARNING' : 'GOOD'] }}>
                {a.value} {a.unit}
              </span>
              <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: a.status === 'ACTIVE' ? '#EF444415' : a.status === 'ACKNOWLEDGED' ? '#F59E0B15' : '#22C55E15', color: a.status === 'ACTIVE' ? '#EF4444' : a.status === 'ACKNOWLEDGED' ? '#F59E0B' : '#22C55E' }}>
                {a.status}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-xs text-center py-4" style={{ color: 'var(--text-muted)' }}>No alerts in the last 30 days</div>
      )}
    </div>
  );
}

export default function EquipmentDetailPanel({ projectId, equipmentCode, device, onBack }) {
  const [telemetry, setTelemetry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const [globalTick, setGlobalTick] = useState(0);

  const fetchDetail = useCallback(async () => {
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/iot/equipment/${equipmentCode}/telemetry/latest`);
      setTelemetry(data);
      setLastUpdate(Date.now());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [projectId, equipmentCode]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  // Auto-refresh every 30s
  useEffect(() => {
    const timer = setInterval(fetchDetail, 30000);
    return () => clearInterval(timer);
  }, [fetchDetail]);

  // Global 1-second ticker
  useEffect(() => {
    const t = setInterval(() => setGlobalTick(g => g + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const healthScore = telemetry?.healthScore || device?.healthScore || 50;
  const params = telemetry?.params || [];
  const healthColor = HEALTH_COLOR(healthScore);

  return (
    <div className="space-y-4" data-testid="iot-detail-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="iot-detail-back">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="text-xs font-mono" style={{ color: 'var(--text-disabled)' }}>{equipmentCode}</div>
            <div className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>{device?.equipmentName || equipmentCode}</div>
          </div>
          <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--hover-bg)', color: 'var(--text-muted)' }}>
            {device?.category?.replace(/_/g, ' ')}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Health Score</div>
            <div className="text-2xl font-bold" style={{ color: healthColor }}>{Math.round(healthScore)}<span className="text-xs font-normal">/100</span></div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchDetail} data-testid="iot-detail-refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="w-5 h-5 animate-spin" style={{ color: 'var(--text-muted)' }} />
        </div>
      ) : (
        <>
          {/* Live Telemetry Grid */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold flex items-center gap-1.5" style={{ color: 'var(--text-primary)' }}>
                <Activity className="w-3.5 h-3.5" style={{ color: '#3B82F6' }} /> Live Sensor Readings
              </h3>
              <div className="flex items-center gap-2" data-testid="global-ticker">
                <span className="inline-block w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#22C55E' }} />
                <Timer className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />
                <span className="text-[10px] font-mono tabular-nums" style={{ color: 'var(--text-muted)' }}>
                  {Math.floor(globalTick / 60).toString().padStart(2,'0')}:{(globalTick % 60).toString().padStart(2,'0')}
                </span>
                <span className="text-[10px]" style={{ color: 'var(--text-disabled)' }}>since load</span>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2" data-testid="telemetry-grid">
              {params.map(p => <TelemetryTile key={p.key} param={p} lastUpdate={lastUpdate} />)}
            </div>
          </div>

          {/* Two-column layout */}
          <div className="grid grid-cols-2 gap-4">
            <TimeSeriesSection projectId={projectId} equipmentCode={equipmentCode} params={params} />
            <HealthBreakdown params={params} />
          </div>

          {/* Predictive Maintenance */}
          <PredictivePanel projectId={projectId} equipmentCode={equipmentCode} />

          {/* Alert History */}
          <AlertHistory projectId={projectId} equipmentCode={equipmentCode} />
        </>
      )}
    </div>
  );
}
